import ISupplierModel from "@models/ISupplierModel";
import ICatalog from "@models/ICatalog";
import ICategory from "@models/ICategory";
import IUser from "@models/IUser";
import CurrenciesEnum from "@models/CurrenciesEnum";
import IndustryNameEnum from "@models/IndustryNameEnum";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

/** `catalog_lock` / `price_group_lock` value for accounts with no lock (the API's VENDOR_FILTER_NO_LOCK). */
export const VENDORS_NO_LOCK = "none";

/** Currencies offered by the account currency picker (CurrencySelect), in its order. */
const CURRENCY_OPTIONS: { title: string; value: CurrenciesEnum }[] = [
    { title: "USD", value: CurrenciesEnum.USD },
    { title: "CAD", value: CurrenciesEnum.CAD },
    { title: "EUR", value: CurrenciesEnum.EUR },
    { title: "GBP", value: CurrenciesEnum.GBP },
    { title: "JPY", value: CurrenciesEnum.JPY },
    { title: "AUD", value: CurrenciesEnum.AUD },
    { title: "MXN", value: CurrenciesEnum.MXN },
    { title: "COP", value: CurrenciesEnum.COP },
];

export interface IVendorsFilterLookups {
    repGroups: (text: string) => Promise<IFilterSelectOption[]>;
    resolveRepGroup?: (guid: string) => Promise<IFilterSelectOption | undefined>;
}

/**
 * Context object with all dynamic data needed to compile the Vendors (Customers) filter options.
 * Fetched once on screen load by the filter bar and passed to the builder.
 */
export interface IVendorsFilterContext {
    supplierModel: ISupplierModel;
    /** Account (vendor) categories */
    categories: ICategory[];
    /** Catalogs with their Price_Groups, for the lock filters */
    catalogs: ICatalog[];
    /** Supplier users, for the sales person filter (suppliers without rep groups) */
    users: IUser[];
    lookups: IVendorsFilterLookups;
    /** Translate a message id (used for static option labels) */
    formatMessage: (id: string) => string;
}

/** Baseline Vendors filters: what "All Accounts", Reset and Clear all return to (the legacy reset sends `filter_inactive=true`). */
export const VENDORS_DEFAULT_FILTERS: FilterValues = {
    filter_inactive: true,
};

/**
 * Compile the Vendors (Customers) filter config. Field keys ARE the wire names of the `getVendors` queryString,
 * so legacy deep links (`/vendors?name=...&filter_inactive=false`) and presets store API param names.
 * Yes/no filters send `Y` / `N` like the legacy state license filter. Visibility gates follow the supplier's features.
 */
export function buildVendorsFilterSections(ctx: IVendorsFilterContext): IFilterSection[] {
    const { supplierModel, formatMessage } = ctx;
    const integration = supplierModel.Supplier_Integration;

    const all: IFilterSelectOption = { label: formatMessage("all"), value: "" };

    const yesNoOptions: IFilterSelectOption[] = [
        { label: formatMessage("any"), value: "" },
        { label: formatMessage("yes"), value: "Y" },
        { label: formatMessage("no"), value: "N" },
    ];

    // ── Section 1: Quick Filters ──
    const quick: IFilterField[] = [
        { key: "name", label: "name", type: "text", apiKey: "name" },
        { key: "email", label: "email", type: "text", apiKey: "email" },
    ];

    if (ctx.categories.length) {
        quick.push({
            key: "category",
            label: "category",
            type: "select",
            apiKey: "category",
            options: [all, ...ctx.categories.map((c) => ({ label: c.name, value: String(c.id) }))],
        });
    }

    if (supplierModel.has_currencies) {
        quick.push({
            key: "currency",
            label: "currency",
            type: "select",
            apiKey: "currency",
            options: [all, ...CURRENCY_OPTIONS.map((c) => ({ label: c.title, value: String(c.value) }))],
        });
    }

    quick.push({ key: "note", label: "note", type: "text", apiKey: "note" });

    // ── Section 2: Sales (rep groups replace per-account sales people when enabled) ──
    const sales: IFilterField[] = [];

    if (supplierModel.has_rep_groups) {
        sales.push({
            key: "rep_group",
            label: "rep_group",
            type: "typeahead",
            apiKey: "rep_group",
            lookup: ctx.lookups.repGroups,
            resolveOption: ctx.lookups.resolveRepGroup,
        });
    } else {
        sales.push({
            key: "sales_person",
            label: "sales_person_s",
            type: "select",
            apiKey: "sales_person",
            options: [all, ...ctx.users.map((u) => ({ label: u.name, value: u.guid }))],
        });
    }

    // ── Section 3: Pricing (catalog / price group locks) ──
    const pricing: IFilterField[] = [];
    const noLock: IFilterSelectOption = { label: formatMessage("none"), value: VENDORS_NO_LOCK };

    if (ctx.catalogs.length) {
        pricing.push({
            key: "catalog_lock",
            label: "catalog_lock",
            type: "select",
            apiKey: "catalog_lock",
            options: [all, noLock, ...ctx.catalogs.map((c) => ({ label: c.name, value: c.guid }))],
        });
    }

    // Price group names repeat across catalogs, so prefix the catalog when there is more than one.
    const priceGroupOptions: IFilterSelectOption[] = ctx.catalogs.flatMap((catalog) =>
        (catalog.Price_Groups || []).map((pg) => ({
            label: ctx.catalogs.length > 1 ? `${catalog.name}: ${pg.value}` : pg.value,
            value: pg.guid,
        }))
    );

    if (priceGroupOptions.length) {
        pricing.push({
            key: "price_group_lock",
            label: "price_group_lock",
            type: "select",
            apiKey: "price_group_lock",
            options: [all, noLock, ...priceGroupOptions],
        });
    }

    // ── Section 4: Compliance ──
    const compliance: IFilterField[] = [
        { key: "resale_tax_id", label: "resale_tax_id", type: "text", apiKey: "resale_tax_id" },
        { key: "has_resale_tax_id", label: "has_resale_tax_id", type: "select", apiKey: "has_resale_tax_id", options: yesNoOptions },
    ];

    if (supplierModel.has_account_state_license) {
        compliance.push({ key: "state_license", label: "state_license", type: "select", apiKey: "state_license", options: yesNoOptions });
    }

    if (supplierModel.Industries?.some((industry) => industry.name === IndustryNameEnum.Firearms)) {
        compliance.push({ key: "ffl_number", label: "ffl_number", type: "text", apiKey: "ffl_number" });
    }

    // ── Section 5: Connected (accounting / ERP integrations the supplier has enabled) ──
    const connected: IFilterField[] = [];

    if (integration?.has_qbo) {
        connected.push({
            key: "qbo_connected",
            label: "quickbooks_online",
            type: "select",
            apiKey: "qbo_connected",
            options: yesNoOptions,
        });
    }

    if (integration?.has_qbd) {
        connected.push({
            key: "qbd_connected",
            label: "quickbooks_enterprise",
            type: "select",
            apiKey: "qbd_connected",
            options: yesNoOptions,
        });
    }

    if (integration?.has_fishbowl) {
        connected.push({
            key: "fishbowl_connected",
            label: "fishbowl",
            type: "select",
            apiKey: "fishbowl_connected",
            options: yesNoOptions,
        });
    }

    // ── Section 6: Filter Out (exclusion) ──
    const filterOut: IFilterField[] = [{ key: "filter_inactive", label: "inactive", type: "toggle", apiKey: "filter_inactive" }];

    return [
        { id: "quick", title: "quick_filters", filters: quick },
        { id: "sales", title: "sales", filters: sales },
        { id: "pricing", title: "pricing", filters: pricing },
        { id: "compliance", title: "compliance", filters: compliance },
        { id: "connected", title: "connected", filters: connected },
        { id: "filterOut", title: "filter_out", isExclusion: true, filters: filterOut },
    ];
}
